import React, { useLayoutEffect, useMemo, useRef } from "react";
import styled from "styled-components";

import { Typography, Spin } from "antd";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

import { useDeepCompareMemoize } from "../../../hooks/useDeepCompareMemoize";

const { Title, Text } = Typography;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  box-shadow: 4px 4px 12px #ccc;
  padding: 20px;
  background: #fff;
`;

const StyledTitle = styled(Title)`
  padding-bottom: 2px;
  border-bottom: 2px solid #eee;
`;

const Chart = styled.div`
  height: 400px;
`;

const SERIES_DEFS = [
  { name: "Audio", field: "audio" },
  { name: "Video", field: "video" },
  { name: "Store and Forward", field: "store and forward" },
];

const ReportClusterChart = ({ title, id, data, loading }) => {
  const rootRef = useRef(null);

  const stableData = useDeepCompareMemoize(data);
  const filteredData = useMemo(
    () => stableData?.filter((item) => item !== undefined) ?? [],
    [stableData]
  );
  const hasData = filteredData.length > 0;

  useLayoutEffect(() => {
    if (!hasData) return undefined;

    const root = am5.Root.new(id);
    rootRef.current = root;
    root.setThemes([am5themes_Animated.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        wheelX: "none",
        wheelY: "none",
        layout: root.verticalLayout,
        maskBullets: false,
      })
    );

    const xRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 20,
      cellStartLocation: 0.1,
      cellEndLocation: 0.9,
    });
    const xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "type",
        renderer: xRenderer,
      })
    );
    xAxis.data.setAll(filteredData);

    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererY.new(root, { minGridDistance: 20 }),
      })
    );

    const legend = chart.children.push(
      am5.Legend.new(root, {
        centerX: am5.percent(50),
        x: am5.percent(50),
        marginTop: 15,
        layout: root.horizontalLayout,
      })
    );

    SERIES_DEFS.forEach(({ name, field }) => {
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name,
          xAxis,
          yAxis,
          valueYField: field,
          categoryXField: "type",
          tooltip: am5.Tooltip.new(root, {
            labelText: "{name}: [bold]{valueY}[/]",
          }),
        })
      );
      series.columns.template.setAll({
        width: am5.percent(90),
        tooltipY: 0,
        strokeOpacity: 0,
      });

      series.bullets.push(() =>
        am5.Bullet.new(root, {
          locationY: 1,
          sprite: am5.Label.new(root, {
            text: "{valueY}",
            fill: am5.color(0x000000),
            centerY: am5.p100,
            centerX: am5.p50,
            populateText: true,
            fontSize: 18,
          }),
        })
      );

      series.data.setAll(filteredData);
      series.appear(800);
      legend.data.push(series);
    });

    chart.appear(800, 100);

    return () => {
      root.dispose();
      rootRef.current = null;
    };
  }, [hasData, filteredData, id]);

  return (
    <Container>
      <StyledTitle level={4}>{title}</StyledTitle>
      {loading && <Spin />}
      {hasData ? (
        <Chart id={id} />
      ) : (
        !loading && <Text>No Data</Text>
      )}
    </Container>
  );
};

export default ReportClusterChart;
